import type { GQAccountItem } from "@/admin/accounts/AccountsTable.tsx";
import type { Action } from "@/components/Action.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { type EnumProps, withEnumBadge } from "@/components/EnumBadge.tsx";
import { useApiClient } from "@/modules/api/apiClient.store.ts";
import { downloadBlob } from "@/utils/downloadBlob.tsx";
import type { KycStatus } from "@warrenio/api-spec/spec.oats.gen";
import { KycProcessStatusEnum } from "@warrenio/api-spec/spec/billingEnums";
import { notNull } from "@warrenio/utils/notNull";

interface KycStatusBadgeActionProps {
    item: GQAccountItem;
    value: KycStatus | undefined;
}

export function KycStatusBadgeButton({ item: { id, kyc_info }, value }: KycStatusBadgeActionProps) {
    const api = useApiClient();
    if (!value) {
        return null;
    }

    let handleClick: Action | undefined;
    if (
        value &&
        kyc_info?.provider === "digio" &&
        [KycProcessStatusEnum.COMPLETED, KycProcessStatusEnum.APPROVED, KycProcessStatusEnum.REJECTED].includes(value)
    ) {
        handleClick = async () => {
            const response = await api.GET("/payment/admin/download_digio_kyc_report", {
                params: { query: { billing_account_id: Number(id) } },
                parseAs: "blob",
            });
            downloadBlob(notNull(response.data, "PDF data"), `digio_kyc_report_${id}.pdf`);
        };
    } else {
        handleClick = undefined;
    }

    return handleClick ? (
        <WButton variant="ghost" action={handleClick}>
            <KycStatusBadge value={value} />
        </WButton>
    ) : (
        <KycStatusBadge value={value} />
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export const kycStatusDescriptions: Record<KycStatus, EnumProps> = {
    not_started: {
        label: "Not started",
        description: "KYC process is not started yet.",
        color: "white",
    },
    started: {
        label: "Started",
        description: "KYC process is started by the user but not completed yet.",
        color: "white",
    },
    completed: {
        label: "Completed",
        description: "KYC process is completed by the user.",
        color: "primary",
    },
    approved: {
        label: "Approved",
        description: "KYC process is completed by the user and also approved by KYC provider.",
        color: "success",
    },
    rejected: {
        label: "Rejected",
        description: "KYC process is completed by the user and also rejected by KYC provider.",
        color: "warning",
    },
};

// eslint-disable-next-line react-refresh/only-export-components
export const KycStatusBadge = withEnumBadge(kycStatusDescriptions);
